import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Network,
  Users,
  Briefcase,
  UserPlus,
  FolderOpen,
  FileText,
  ChevronDown,
  ChevronRight,
  ChevronLeft, // Added ChevronLeft for the close arrow
  LayoutDashboard,
} from "lucide-react";

// Added specific color classes for each icon
const MENU_ITEMS = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/",
    icon: LayoutDashboard,
    iconColor: "text-blue-500",
  },
  {
    id: "organizations",
    label: "Organizations",
    path: "/organizations",
    icon: Building2,
    iconColor: "text-indigo-500",
  },
  {
    id: "org-structure",
    label: "Organization Structure",
    path: "/org-structure/dashboard",
    icon: Network,
    iconColor: "text-purple-500",
    subTabs: [
      {
        id: "dashboard",
        label: "Dashboard",
        path: "/org-structure/dashboard",
      },
      {
        id: "departments",
        label: "Departments",
        path: "/org-structure/departments",
      },
    ],
  },
  {
    id: "employee",
    label: "Employee",
    path: "/employee/dashboard",
    icon: Users,
    iconColor: "text-emerald-500",
    subTabs: [
      { id: "dashboard", label: "Dashboard", path: "/employee/dashboard" },
      { id: "directory", label: "Directory", path: "/employees" },
    ],
  },
  {
    id: "employment",
    label: "Employment",
    path: "/employment/dashboard",
    icon: Briefcase,
    iconColor: "text-amber-500",
    subTabs: [
      {
        id: "employment-dashboard",
        label: "Dashboard",
        path: "/employment/dashboard",
      },
      {
        id: "employment-page",
        label: "Employees & Contracts",
        path: "/employment/employmentpage",
      },
      { id: "schedule", label: "Work Schedule", path: "/employment/schedule" },
    ],
  },

  {
    id: "hr",
    label: "HR",
    path: "/hr/dashboard",
    icon: UserPlus,
    iconColor: "text-rose-500",
    subTabs: [
      { id: "dashboard", label: "Dashboard", path: "/hr/dashboard" },
      { id: "employee", label: "Employee", path: "/hr/employee" },
      { id: "asssets", label: "Assets", path: "/hr/assets" },
      { id: "leaves", label: "Leaves", path: "/hr/leaves" },
      { id: "onboarding", label: "Onboarding", path: "/hr/onboarding" },
    ],
  },
  {
    id: "project",
    label: "Project",

    icon: FolderOpen,
    iconColor: "text-cyan-500",
    path: "/projects",
  },
  {
    id: "dsr",
    label: "DSR",
    path: "/dsr",
    icon: FileText,
    iconColor: "text-orange-500",
  },
];

export default function Sidebar() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => setIsExpanded(!isExpanded);

  const isActive = (path) => location.pathname === path;
  const isSubMenuActive = (subTabs) =>
    subTabs?.some((tab) => location.pathname === tab.path);

  const isMenuOpen = (item) => {
    return !!openMenus[item.id];
  };

  const handleMenuClick = (item) => {
    if (!isExpanded) setIsExpanded(true);

    const currentlyOpen = isMenuOpen(item);

    if (currentlyOpen) {
      // 2nd click on the active/open main menu item: collapse/close the dropdown
      setOpenMenus({ [item.id]: false });
    } else {
      // 1st click or switching to another main menu:
      // Open this item's dropdown and close all other menus
      setOpenMenus({ [item.id]: true });

      // Navigate to the module path (dashboard) if present
      if (item.path) {
        navigate(item.path);
      }
    }
  };

  const handleChevronClick = (e, item) => {
    e.stopPropagation();
    if (!isExpanded) setIsExpanded(true);
    const currentlyOpen = isMenuOpen(item);
    if (currentlyOpen) {
      setOpenMenus((prev) => ({ ...prev, [item.id]: false }));
    } else {
      setOpenMenus({ [item.id]: true });
    }
  };

  const handleSubTabClick = (itemId) => {
    // Close all open dropdowns when any sub-tab link is clicked
    setOpenMenus({});
  };

  return (
    <aside
      className={`bg-white border-r border-slate-200 text-slate-700 h-screen transition-all duration-300 ease-in-out flex flex-col flex-shrink-0 ${
        isExpanded ? "w-1/6" : "w-20"
      }`}
    >
      <div className="h-16 flex items-center justify-between px-6">
        {isExpanded && (
          <span className="text-xl font-bold text-slate-900 tracking-wide">
            CompanyOS
          </span>
        )}
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors mx-auto lg:mx-0"
        >
          {isExpanded ? (
            <ChevronLeft size={24} className="text-slate-500" />
          ) : (
            <ChevronRight size={24} className="text-slate-500" />
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-slate-200">
        <ul className="space-y-1 px-3">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const hasSubTabs = item.subTabs && item.subTabs.length > 0;

            const menuOpen = isMenuOpen(item);
            const active =
              isActive(item.path) ||
              (hasSubTabs && isSubMenuActive(item.subTabs));

            return (
              <li key={item.id} className="flex flex-col">
                {hasSubTabs ? (
                  <button
                    onClick={() => handleMenuClick(item)}
                    className={`flex items-center justify-between w-full px-3 py-3 rounded-lg cursor-pointer transition-colors ${
                      active
                        ? "bg-indigo-50 text-indigo-700 font-medium"
                        : "hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <Icon
                        size={22}
                        className={`min-w-[22px] ${item.iconColor}`}
                      />
                      {isExpanded && (
                        <span className="whitespace-nowrap">{item.label}</span>
                      )}
                    </div>
                    {isExpanded &&
                      (menuOpen ? (
                        <ChevronDown
                          size={18}
                          className="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                          onClick={(e) => handleChevronClick(e, item)}
                        />
                      ) : (
                        <ChevronRight
                          size={18}
                          className="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                          onClick={(e) => handleChevronClick(e, item)}
                        />
                      ))}
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setOpenMenus({})}
                    className={`flex items-center gap-4 px-3 py-3 rounded-lg cursor-pointer transition-colors ${
                      active
                        ? "bg-indigo-50 text-indigo-700 font-medium"
                        : "hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <Icon
                      size={22}
                      className={`min-w-[22px] ${item.iconColor}`}
                    />
                    {isExpanded && (
                      <span className="whitespace-nowrap">{item.label}</span>
                    )}
                  </Link>
                )}

                {hasSubTabs && isExpanded && menuOpen && (
                  <ul className="mt-1 mb-2 space-y-1 ml-9 border-l border-slate-200 pl-2">
                    {item.subTabs.map((subTab) => {
                      const isCurrentlyActive = isActive(subTab.path);

                      return (
                        <li key={subTab.id}>
                          <Link
                            to={subTab.path}
                            onClick={() => handleSubTabClick(item.id)}
                            className={`block px-4 py-2 text-sm rounded-md transition-colors ${
                              isCurrentlyActive
                                ? "bg-indigo-50 text-indigo-700 font-medium"
                                : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                            }`}
                          >
                            {subTab.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
